"use client";

import NumberInput from "@components/inputs/Number";
import { useMarket } from "@context/marketContext";
import { Slider } from "@components/ngg";
import type { Filter as FilterType } from "@server/utils/parseLoop/types";
import { type ChangeEvent, type FormEvent, useEffect, useState } from "react";

type Props<Attribute, Value> = FilterType & {
  min: number;
  max: number;
  values: Value;
  attribute: Attribute;
  onChange: (
    event: ChangeEvent<HTMLInputElement>,
    attribute: Attribute,
  ) => void;
  maxLabel?: string;
  minLabel?: string;
  suffix?: string;
};
export default function ExponentialRange<Attribute, Value>(
  props: Props<Attribute, Value>,
) {
  const { locale } = useMarket().state.market;
  const { min, max, onChange, attribute, values } = props;
  const [localValue, setLocalValue] = useState(values ?? [min, max]);
  const handleCommit = (val: number[]) => {
    const [min, max] = val;
    // @todo Do not use any
    onChange({ target: { min, max } } as any, attribute);
  };
  const handleChange = (val: number[]) => {
    setLocalValue(val);
  };
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.target as HTMLFormElement);
    const minValue = Number(data.get("minValue"));
    const maxValue = Number(data.get("maxValue"));
    const [actualMin, actualMax] = [minValue, maxValue].sort((a, b) => a - b);
    const newValue = {
      target: {
        type: "range",
        min: actualMin,
        max: actualMax,
      },
    } as unknown;
    onChange(newValue as ChangeEvent<HTMLInputElement>, attribute);
  };
  useEffect(() => values && setLocalValue(values), [values]);
  return (
    <div className="pb-4">
      <Slider
        value={localValue}
        min={min}
        max={max}
        exponentialScaling
        onCommit={handleCommit}
        onChange={handleChange}
      />
      <form
        onSubmit={handleSubmit}
        className="flex w-full justify-between overflow-hidden"
      >
        <div className="grid shrink gap-y-2">
          <NumberInput
            id="minValue"
            min={min}
            max={max}
            name="minValue"
            label={props.minLabel ?? ""}
            locale={locale}
            alignLabel="left"
            value={localValue[0]}
            suffix={props.attribute === "price" ? "%CURRENCY%" : props.suffix}
          />
        </div>
        <button type="submit" className="invisible" aria-label="Submit" />
        <div className="grid shrink gap-y-2">
          <NumberInput
            id="maxValue"
            min={min}
            max={max}
            name="maxValue"
            label={props.maxLabel ?? ""}
            locale={locale}
            alignLabel="right"
            value={localValue[1]}
            suffix={props.attribute === "price" ? "%CURRENCY%" : props.suffix}
          />
        </div>
      </form>
    </div>
  );
}
